import React, { useState, useEffect, Suspense, lazy } from 'react';
import {
  Check, X, ArrowRight, Building2, Tags, BarChart3, FileText, ShieldCheck, Sparkles, Minus, Plus, Sun, Moon, Box, Route, Map, Instagram, Linkedin, Youtube, ChevronUp, LayoutDashboard, Smartphone, Tablet, Monitor
} from 'lucide-react';
import { BASE_MONTHLY, BASE_ANNUAL, SEAT_MONTHLY, SEAT_ANNUAL, ANNUAL_SAVINGS_LABEL, TRIAL_DAYS } from '../utils/pricing';
import LegalDocModal from '../components/LegalDocModal';
import ImageLightbox from '../components/ImageLightbox';
import { trackTrialCtaClick, trackDemoStart } from '../utils/analytics';

// lucide-react only ships the old bird logo (exported as `Twitter`) — wrong
// mark for the current X rebrand, so this is the actual X glyph inlined as
// an SVG instead. Named XLogoIcon, not `X`, to avoid colliding with
// lucide's own `X` (the close-icon glyph) already imported above.
function XLogoIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l12 15.644Z" />
    </svg>
  );
}

// Same reasoning as XLogoIcon — Threads (Meta) is too new for lucide-react
// to ship a mark for, so it's inlined here too.
function ThreadsLogoIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.023v-.042c.028-3.579.879-6.43 2.529-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.593 12c.023 3.086.714 5.496 2.052 7.164 1.43 1.781 3.63 2.695 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.107-4.798-.31-.735-.869-1.35-1.639-1.82-.19 1.395-.635 2.518-1.328 3.34-.926 1.1-2.238 1.708-3.9 1.804-1.253.073-2.462-.223-3.403-.833-1.11-.72-1.766-1.816-1.845-3.086-.076-1.24.377-2.399 1.276-3.264.86-.827 2.072-1.32 3.508-1.415a19.6 19.6 0 0 1 2.09 0c-.086-.62-.26-1.104-.522-1.44-.36-.462-.916-.699-1.653-.706h-.028c-.65 0-1.522.166-2.074.918l-1.85-1.297c.719-1.04 2.184-2.28 4.39-2.263 2.15.017 3.618.856 4.394 2.5 1.244.487 2.176 1.328 2.777 2.502.99 1.933 1.028 5.09-1.577 7.51-1.98 1.842-4.376 2.667-7.775 2.667zm.906-11.63c-.098 0-.196.002-.294.006-1.087.06-1.926.44-2.293.919-.303.398-.407.905-.297 1.469.164.83.98 1.235 1.99 1.176.997-.06 1.83-.44 2.164-1.01.283-.484.33-1.16.15-2.031a5.99 5.99 0 0 0-1.42-.529z" />
    </svg>
  );
}

// One entry per confirmed live account — QuoteHaul's LinkedIn, X, Instagram,
// and Threads accounts went live 2026-08-17, YouTube shortly after
// (project memory: project_social_accounts.md); more will be added here as
// they're confirmed, same shape (href + label + icon), rather than each new
// one needing its own bespoke footer markup.
const SOCIAL_LINKS = [
  { href: 'https://www.linkedin.com/showcase/quotehaul', label: 'QuoteHaul on LinkedIn', Icon: Linkedin },
  { href: 'https://x.com/QuoteHaulApp', label: 'QuoteHaul on X', Icon: XLogoIcon },
  { href: 'https://www.instagram.com/quotehaul', label: 'QuoteHaul on Instagram', Icon: Instagram },
  { href: 'https://www.threads.com/@quotehaul', label: 'QuoteHaul on Threads', Icon: ThreadsLogoIcon },
  { href: 'https://www.youtube.com/@QuoteHaulApp', label: 'QuoteHaul on YouTube', Icon: Youtube },
];

// Lazy-loaded: this is the only thing on the whole landing page that pulls
// in real Firebase Auth/Firestore (see TrialFlow.jsx) — the dynamic import()
// only fires once a visitor actually clicks "Start Your Trial," so everyone
// just browsing never downloads it at all.
const TrialFlow = lazy(() => import('./TrialFlow.jsx'));

// Filenames match public/img/marketing/ (trimmed of the source tool's
// leading timestamp prefix). This same folder doubles as the source for any
// future HelpPage.jsx tutorial images too — a separate flat/unstyled
// public/img/tutorials/ used to exist for that, but was removed once it
// became a pure duplicate of this folder's content.
//
// Ordered to match FEATURE_SPOTLIGHTS' reading order below (index-grouped
// there), not alphabetically or by capture date — this is also the order
// ProductTourLightbox's prev/next cycles through, so the lightbox order
// matches the order a visitor actually scrolled past them in.
const PRODUCT_TOUR_SHOTS = [
  { file: 'dashboard-overview-stats.webp', title: 'Live Dashboard', desc: 'See your whole book of business at a glance.', alt: 'QuoteHaul dashboard showing shipment stats overview' },
  { file: 'shipment-cargo-visualization.webp', title: '3D Cargo Visualization', desc: 'Know it fits before you quote it — in AR, on device.', alt: 'Interactive 3D cargo visualization inside a trailer' },
  { file: 'multistop-load-cargo-visualization.webp', title: 'Combined Load Visualization', desc: 'Every stop\'s cargo, color-coded in one shared 3D truck view.', alt: '3D visualization of a multi-stop combined truckload' },
  { file: 'freight-analytics-multistop-loads.webp', title: 'Multi-Stop Loads Report', desc: 'See performance across combined, multi-pickup shipments.', alt: 'Multi-stop loads analytics report' },
  { file: 'organizations-directory-list.webp', title: 'Organizations Directory', desc: 'Every shipper, consignee, and carrier, saved once.', alt: 'Organizations directory list' },
  { file: 'rate-cards-management.webp', title: 'Negotiated Rate Cards', desc: 'Your real carrier rates, not a spot-market guess.', alt: 'Rate card management screen' },
  { file: 'trucking-quote-pdf-preview.webp', title: 'Instant Paperwork', desc: 'BOLs, invoices, and quote PDFs generated in one click.', alt: 'Generated freight quote PDF preview' },
  { file: 'shipping-label-barcode.webp', title: 'Shipping Labels', desc: 'Barcoded 4"×6" labels, one per physical piece, generated straight from the shipment.', alt: 'Barcoded 4x6 shipping label generated from a shipment' },
  { file: 'freight-analytics-overview.webp', title: 'Freight Analytics', desc: 'Margin, volume, and win-loss trends, not just one quote.', alt: 'Freight margin and analytics dashboard' },
  { file: 'freight-analytics-shipping-lanes-map.webp', title: 'Shipping Lanes Map', desc: 'Every lane you\'ve quoted, mapped across the country.', alt: 'US map showing shipping lane density between states' },
  // Inserted here (not appended) despite every later index shifting as a
  // result — confirmed real bug: an earlier version appended this at the
  // end to avoid renumbering, but ImageLightbox's prev/next cycles through
  // this whole flat array in order, so "next" from the map (this array's
  // own documented cycling order — see top-of-file comment) jumped straight
  // to Team & User Management, skipping over this shot entirely; only its
  // own direct thumbnail click ever reached it. Reading order has to be the
  // real array order, not just FEATURE_SPOTLIGHTS' shots-index grouping.
  { file: 'freight-analytics-shipping-lanes-california-report.webp', title: 'State Lane Report', desc: 'Click any state for its own inbound/outbound breakdown.', alt: 'California state lane report showing inbound and outbound shipment breakdown' },
  { file: 'settings-team-user-management.webp', title: 'Team & User Management', desc: 'Seats, roles, and admin controls for your whole team.', alt: 'Team and user management settings screen' },
  { file: 'admin-tasks-review-queue.webp', title: 'Admin Approvals', desc: 'Deleting an approved or voided record requires admin sign-off, tracked start to finish.', alt: 'Admin task queue reviewing a pending approval request' },
  { file: 'my-requests-pending-approval.webp', title: 'My Requests', desc: 'Every teammate can track the status of what they\'ve submitted.', alt: 'My Requests page showing a pending approval request' },
];

// Full-resolution, single-device versions of the same three screenshots
// composited into device-compatibility.webp below — used as the lightbox
// target when a visitor clicks/taps that device in the composite, so the
// enlarged view isn't just a blown-up crop of the smaller flattened image.
// Kept as its own flat array (like PRODUCT_TOUR_SHOTS) so DeviceCompatSection's
// lightbox can prev/next cycle through just these three, independent of the
// much longer product-tour gallery.
const DEVICE_SHOTS = [
  { file: 'device-desktop-full.webp', title: 'Desktop', desc: 'Every module, one click away.', alt: 'QuoteHaul running in a desktop browser' },
  { file: 'device-tablet-full.webp', title: 'Tablet', desc: 'Full dashboard, built for touch.', alt: 'QuoteHaul running on a tablet' },
  { file: 'device-phone-full.webp', title: 'Phone', desc: 'Shipment status, right in your pocket.', alt: 'QuoteHaul running on a phone' },
];

// Merges what used to be two separate, largely redundant sections — a
// screenshot grid (show) and a plain icon/text feature grid (tell) that
// told the same product story twice. Each row now pairs the real
// screenshot(s) with the fuller descriptive copy in one glance, alternating
// image side for visual rhythm. `shots` indexes into PRODUCT_TOUR_SHOTS
// (kept as the flat source of truth for ProductTourLightbox's prev/next);
// a row with 2-3 shots renders them as a small stacked/side-by-side
// cluster instead of one big image. Document Attachments (a FEATURES-only
// entry with no dedicated screenshot of its own) is folded into the
// Paperwork row's copy rather than given an empty-handed row of its own.
const FEATURE_SPOTLIGHTS = [
  {
    icon: LayoutDashboard,
    title: 'Live Dashboard',
    shots: [0],
    desc: 'See your whole book of business at a glance — every quote and shipment, its status, and where it stands, updated in real time as your team works.',
  },
  {
    icon: Box,
    badge: 'AR',
    title: 'Freight Builder & 3D Cargo Visualization',
    shots: [1],
    desc: 'Price the load and visualize exactly how it packs into the truck or trailer in one guided flow, from first quote to final delivery. Drag to rotate, switch equipment types, and see instantly whether it actually fits before you quote it, not after. On supported iOS and Android devices, tap View in AR to place that exact load in your own space at true, real-world scale through your camera.',
  },
  {
    icon: Route,
    title: 'Multi-Stop Loads',
    shots: [2, 3],
    desc: 'Combine several quotes onto one shared truck with multiple pickups and drops — track its own status from booked to delivered, see every shipment\'s cargo color-coded in one 3D view, and pull each stop\'s BOL and labels straight from its linked quote.',
  },
  {
    icon: Building2,
    title: 'Organizations Directory',
    shots: [4],
    desc: 'A saved directory of every shipper, consignee, customer, and carrier — no retyping contact details quote after quote.',
  },
  {
    icon: Tags,
    title: 'Negotiated Rate Cards',
    shots: [5],
    desc: 'Save per-lane rates with your carriers so the builder offers your real negotiated price instead of a spot-market guess.',
  },
  {
    icon: FileText,
    title: 'Paperwork, Invoicing & Shipping Labels',
    shots: [6, 7],
    desc: 'Generate a Bill of Lading, customer invoice, or barcoded 4"×6" shipping labels straight from an accepted quote — no separate paperwork tool. Attach any supporting file straight to the shipment or organization record too, so everything lives in one place instead of scattered across email.',
  },
  {
    icon: BarChart3,
    title: 'Freight Margin Analytics',
    shots: [8],
    desc: 'See revenue, margin, and volume trends across your team, not just a single quote at a time.',
  },
  {
    icon: Map,
    title: 'Shipping Lanes Map',
    shots: [9, 10],
    desc: "See your busiest lanes — mapped, not guessed. Every lane you've quoted, plotted across the country. Busier lanes stand out at a glance, colors are yours to customize, and you can export the map as an image or PDF whenever you need it. Click any state for its own inbound/outbound breakdown.",
  },
  {
    icon: ShieldCheck,
    title: 'Team Roles & Admin Controls',
    shots: [11, 12, 13],
    desc: 'Invite your team with real seat management, Admin/Manager/Member roles, and admin approval workflows enforced server-side — plus every teammate can track the status of their own requests.',
  },
];

// Grounded in the app's actual gating (src/utils/navItems.js's isPro flags +
// the dual-persistence pattern — guest/demo mode works entirely off
// localStorage, no Firestore sync), not aspirational marketing copy. There's
// no separate paid "free tier" in this app (see the Pricing section above —
// one plan, every feature) — the free side of this comparison is the
// no-signup local demo mode, not a lesser paid plan.
//
// Organizations Directory and Negotiated Rate Cards used to be listed as
// flatly unavailable in demo mode (demo: false) — no longer accurate. Demo
// mode now auto-seeds sample data and lets a visitor browse both directly
// (src/components/Sidebar.jsx's DEMO_UNLOCKED_NAV_IDS — My Requests is
// unlocked there too, just not called out as its own row on this table);
// only creating/editing your own real records still requires Pro, hence the
// "Browse only" framing rather than a bare checkmark.
const COMPARISON_ROWS = [
  { label: 'Freight Builder (quote calculator)', demo: 'Sample use', pro: true },
  { label: 'Cargo Visualization (3D layout view + AR on supported devices)', demo: true, pro: true },
  { label: 'Equipment Suggestion (auto-recommends the cheapest fit)', demo: true, pro: true },
  { label: 'Multi-Stop Loads (combine quotes onto one shared truck)', demo: true, pro: true },
  { label: 'Default Rates & Pricing', demo: false, pro: true },
  { label: 'Organizations Directory', demo: 'Browse only, with sample data', pro: true },
  { label: 'Negotiated Rate Cards', demo: 'Browse only, with sample data', pro: true },
  { label: 'BOL, Invoice & Shipping Label Generation', demo: false, pro: true },
  { label: 'Document Attachments', demo: false, pro: true },
  { label: 'Freight Margin Analytics', demo: false, pro: true },
  { label: 'Shipping Lanes Map (US map + state-level drilldown)', demo: false, pro: true },
  { label: 'Team Roles — Admin, Manager & Member', demo: false, pro: true },
];

const FAQS = [
  { q: 'What is QuoteHaul?', a: 'QuoteHaul is freight quoting and shipment management software for brokers, shippers, and carriers — build a quote, price it, generate the paperwork, and track it through delivery.' },
  { q: 'Is QuoteHaul a full TMS?', a: 'No — it\'s a lighter, faster alternative for brokers and shippers who need quoting, 3D cargo planning, and paperwork without the cost or complexity of a full TMS implementation. If you need carrier GPS tracking, EDI, or dispatch, a full TMS is a better fit; if you need to price a load, see how it fits, and get it booked and documented fast, QuoteHaul is built for exactly that.' },
  { q: 'Can I try it before paying anything?', a: `Yes — try the Freight Builder free with no signup at all (local demo mode), or start a ${TRIAL_DAYS}-day free trial of the full Pro plan. Your card isn't charged until the trial ends.` },
  { q: 'What happens after the free trial?', a: `Your subscription renews automatically at the plan price you selected unless you cancel first. You can cancel anytime before the trial ends with no charge.` },
  { q: 'Do I have to start with the free trial?', a: 'No — if you\'d rather skip it, choose "Buy Now" at checkout to start your Pro subscription immediately with no trial period. Same price either way, just no wait.' },
  { q: 'Can I add teammates?', a: 'Yes — invite teammates as additional seats on your subscription, with admin-controlled roles and access.' },
  { q: 'Does the AR cargo view work on every phone?', a: 'AR is available on supported iOS and Android devices with a camera and AR hardware support (Quick Look on iOS, Scene Viewer on Android) — it isn\'t available on desktop, and not every phone or tablet supports it. On iPhone/iPad, Safari loads AR noticeably faster than Chrome, since Quick Look is built directly into Safari. The 3D cargo view itself (drag to rotate, zoom, switch equipment) works everywhere, with or without AR.' },
];

function formatUsd(n) {
  return `$${n}`;
}

// Deliberately its OWN storage key, separate from the app's own
// 'quotehaul_theme' (the one set/saved via a signed-in account's Settings) —
// confirmed bug from sharing that key: App.jsx defaults to 'dark' whenever
// nothing is stored, but its effect immediately persists that default BACK
// to 'quotehaul_theme' on every single app visit, even if the visitor never
// touches the theme toggle in Settings. This page's theme has nothing to do
// with that account-level setting; a dedicated key keeps it fully unrelated.
//
// Split into two pieces of state on purpose, not one:
//  - explicitTheme: only ever set by THIS page's own toggle below, and only
//    then persisted to localStorage. null until the visitor actually clicks it.
//  - systemTheme: the OS's prefers-color-scheme, tracked LIVE via a
//    matchMedia listener (not just read once at mount) so this page keeps
//    following the OS setting in real time — including an OS-level light/dark
//    switch (e.g. an auto night-mode schedule) while the tab is already open.
// Collapsing these into one persisted value (the previous version) meant the
// very first resolved value got written to storage and then replayed on
// every later visit — so it only ever reflected the OS setting once, not
// "the system preferred theme," which is what was actually asked for.
function getSystemTheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return 'dark';
}

// Preserved exactly as the pre-redesign landing page — kept as an escape
// hatch behind the Dev Tools "Landing Page Theme" toggle (localStorage-only,
// system-admin gated) in case the new design needs to be rolled back on a
// given browser without a deploy. Not rendered for real visitors by default.
export default function LandingPageLegacy() {
  const [explicitTheme, setExplicitTheme] = useState(() => {
    try {
      const stored = localStorage.getItem('quotehaul_landing_theme');
      return stored === 'light' || stored === 'dark' ? stored : null;
    } catch (e) { return null; }
  });
  const [systemTheme, setSystemTheme] = useState(getSystemTheme);
  const theme = explicitTheme || systemTheme;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => setSystemTheme(e.matches ? 'light' : 'dark');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('light', theme === 'light');
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  function toggleTheme() {
    const next = theme === 'light' ? 'dark' : 'light';
    setExplicitTheme(next);
    try { localStorage.setItem('quotehaul_landing_theme', next); } catch (e) {}
  }

  const [billingCycle, setBillingCycle] = useState('monthly');
  // Chosen here on the pricing card and passed straight into TrialFlow below
  // as props — so the plan/seat count a visitor picks BEFORE creating an
  // account is already pre-filled once they reach checkout, instead of
  // asking them to re-pick from scratch right after signup.
  const [extraSeats, setExtraSeats] = useState(0);
  const [legalDoc, setLegalDoc] = useState(null);
  // Index into PRODUCT_TOUR_SHOTS for the currently-expanded lightbox, or
  // null when closed — lets a visitor actually see detail in these
  // screenshots instead of squinting at a ~380px-wide thumbnail.
  const [expandedShotIdx, setExpandedShotIdx] = useState(null);
  const [expandedDeviceIdx, setExpandedDeviceIdx] = useState(null);
  // Opens the in-page trial flow (plan/seat selection -> account creation ->
  // auto-resume checkout) instead of navigating to /ops/?auth=signup.
  const [trialFlowOpen, setTrialFlowOpen] = useState(false);
  // Floating "back to top" button — only shown once a visitor has actually
  // scrolled past the hero, so it isn't just sitting there uselessly on
  // first load. One scroll listener for the whole page rather than a
  // per-section observer, since the show/hide threshold is a single simple
  // "scrolled past N px" check, not tied to any specific section boundary.
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    function handleScroll() {
      setShowBackToTop(window.scrollY > 600);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Header "Sign In" link — needs to know whether the visitor already has a
  // session BEFORE deciding where to send them (straight to their dashboard
  // vs. /ops/?auth=login to force the login modal), but this file
  // deliberately has zero Firebase imports at the top level (see this
  // module's own comment on PRODUCT_TOUR_SHOTS/TrialFlow) so the whole SDK
  // never loads for a visitor who's just browsing. Loading it on-click,
  // lazily, the same way TrialFlow.jsx is only pulled in once "Start Your
  // Trial" is actually clicked, keeps that guarantee intact.
  const [checkingSignIn, setCheckingSignIn] = useState(false);
  async function handleSignInClick(e) {
    e.preventDefault();
    if (checkingSignIn) return;
    setCheckingSignIn(true);
    // Deliberate, one-time hand-off of THIS page's current theme into the
    // app's own storage key, only at the moment of this explicit click —
    // NOT a passive sync between the two keys generally, which is exactly
    // what quotehaul_landing_theme was split out from quotehaul_theme to
    // avoid in the first place (see this component's own theme state
    // comment: sharing one key meant simply visiting the app as a guest
    // silently overwrote whatever a landing-page visitor had chosen, since
    // the app's own theme-persist effect always writes something back on
    // every visit). A real, explicit "take me to the app" click is exactly
    // the one moment carrying the choice over is actually wanted.
    try { localStorage.setItem('quotehaul_theme', theme); } catch (e) {}
    try {
      const { auth } = await import('../firebase.js');
      await auth.authStateReady();
      window.location.href = auth.currentUser ? '/ops/' : '/ops/?auth=login';
    } catch (err) {
      // Firebase failed to load or initialize for any reason — fail open to
      // the login-prompt path rather than leaving the link dead.
      window.location.href = '/ops/?auth=login';
    }
  }
  // TrialFlow mounts inline on this same document, so it already inherits
  // this page's live theme with no hand-off needed. But once signup inside
  // it completes, it navigates into the real app (a separate document) —
  // same hand-off as handleSignInClick above, so that eventual page load
  // also picks up the theme the visitor had here.
  function handleStartTrialClick(location) {
    trackTrialCtaClick(location);
    try { localStorage.setItem('quotehaul_theme', theme); } catch (e) {}
    setTrialFlowOpen(true);
  }
  const basePrice = billingCycle === 'annual' ? BASE_ANNUAL : BASE_MONTHLY;
  const seatPrice = billingCycle === 'annual' ? SEAT_ANNUAL : SEAT_MONTHLY;
  const totalPrice = basePrice + seatPrice * extraSeats;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      {/* sticky (not fixed) so it stays in normal flow until scrolled to,
          then pins in place — no extra top-padding needed on the content
          below to avoid an initial overlap the way `fixed` would require.
          z-20 keeps it above the hero section's own z-10 (see below), so it
          doesn't get covered by the hero's overflowing background logo when
          scrolling past it. bg-slate-950/80 + backdrop-blur: 80% opacity so
          it's mostly solid but content is still faintly visible/blurred
          underneath while scrolling, rather than a hard, flat bar. */}
      <header className="sticky top-0 z-20 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/80">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
            <img src="/img/quotehaul-logo 600px.webp" alt="QuoteHaul" className="h-7 sm:h-9 w-auto object-contain shrink-0" />
            <div className="min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
                <span className="text-base sm:text-lg font-bold text-white leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>QuoteHaul</span>
                <span className="text-slate-500 text-[10px] sm:text-xs leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>by XAYVR Company</span>
              </div>
              <p className="hidden sm:block text-slate-400 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>Smart freight quoting, visualized.</p>
            </div>
          </div>

          {/* In-page anchor links — hidden below sm since the header is
              already tight there (logo + theme toggle + Go to App + CTA), and
              a visitor scrolling on mobile reaches these sections directly
              anyway. scroll-mt-20 on each target section (not here) is what
              keeps the sticky header from covering the section heading when
              the browser jumps to it. */}
          <nav className="hidden sm:flex items-center gap-5 shrink-0">
            <a href="#features" className="text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap">Features</a>
            <a href="#pricing" className="text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap">Pricing</a>
            <a href="#faq" className="text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap">FAQ</a>
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={toggleTheme}
              className="p-1.5 sm:p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition shrink-0"
              title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            >
              {theme === 'light' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            {/* href is the no-JS fallback (goes straight to the login
                prompt); handleSignInClick intercepts the real click to
                check for an existing session first — see its own comment
                above for why that check has to be lazy. Signed-in visitors
                land straight in their dashboard, no modal in the way;
                everyone else gets /ops/?auth=login, which App.jsx's
                authIntent effect turns into an auto-opened login form. */}
            <a
              href="/ops/?auth=login"
              onClick={handleSignInClick}
              aria-disabled={checkingSignIn}
              className="text-xs sm:text-sm font-semibold text-slate-300 hover:text-white transition whitespace-nowrap"
            >
              {checkingSignIn ? 'One moment…' : 'Sign In'}
            </a>
            <button
              type="button"
              onClick={() => handleStartTrialClick('header')}
              className="px-2.5 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs sm:text-sm font-bold rounded-lg shadow-lg shadow-blue-600/20 transition whitespace-nowrap"
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section className="relative z-10">
        {/* Decorative background watermark — right-justified, purely visual
            (aria-hidden + empty alt: the real logo/name is already in the
            nav above, this would just be redundant noise for screen
            readers). Low opacity + pointer-events-none so it never competes
            with or blocks the actual hero content in front of it.
            No overflow utility at all on this section (real fix, replacing
            a wrong one) — the image is taller than the hero's own content
            height, so it needs to render outside the section's own box.
            overflow-x-hidden was tried first, but per the CSS spec, setting
            overflow-x to anything but "visible" while overflow-y is left
            default silently upgrades overflow-y to "auto" too — which still
            clips content that doesn't fit, just conditionally with a
            scrollbar, so the image was never actually escaping vertically
            at all. Safe to leave fully unclipped here since the image is
            anchored with right-0 inside this already-full-width section, so
            it can't bleed past the right edge of the viewport regardless.
            z-10 (relative, above) is what keeps the overflow from being
            painted over by the next section below, which — being a later,
            unpositioned sibling — would otherwise paint on top of it by
            default. */}
        <img
          src="/img/quotehaul-logo 600px.webp"
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute right-0 top-1/2 -translate-y-1/2 w-[420px] sm:w-[560px] lg:w-[640px] max-w-none opacity-[0.34]"
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 pt-16 pb-14 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-600/20 border border-blue-500/40 rounded-full text-blue-400 text-xs font-bold uppercase tracking-wider mb-6">
            <Sparkles className="w-3.5 h-3.5" /> Smart freight quoting, visualized
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white leading-tight mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            See it fit before it ships.
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto mb-8">
            QuoteHaul prices the load, shows you exactly how it packs into the truck in 3D — view it in AR on
            supported phones — and generates the BOL, all before a driver ever shows up. Built for freight brokers, 3PLs,
            freight forwarders, and shippers.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => handleStartTrialClick('hero')}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-500/25 transition"
            >
              <span>Start Your {TRIAL_DAYS}-Day Free Trial</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="/ops/"
              onClick={trackDemoStart}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-bold text-sm rounded-xl transition"
            >
              Try the Demo — No Signup
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-4">Card required to start a trial. No charge until day {TRIAL_DAYS}. Cancel anytime before then.</p>
        </div>
      </section>

      {/* ── DEVICE COMPATIBILITY ────────────────────────────────────────── */}
      {/* A distinct message beat from the feature spotlight below — WHERE it
          works, not WHAT it does — so it gets its own short section right
          after the hero rather than being folded into either the hero
          (its low-opacity background art would wash this out) or the
          feature-by-feature story further down. */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 text-center">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          One workspace. Every screen.
        </h2>
        <p className="text-sm text-slate-400 max-w-xl mx-auto mb-3">
          Quote from your desktop, check a shipment's status from your phone on the dock, walk a tablet through cargo placement in the yard — same real data, everywhere.
        </p>
        <div className="flex items-center justify-center gap-4 sm:gap-6 text-slate-500 text-xs font-semibold mb-10">
          <span className="flex items-center gap-1.5"><Smartphone className="w-3.5 h-3.5" /> Phone</span>
          <span className="flex items-center gap-1.5"><Tablet className="w-3.5 h-3.5" /> Tablet</span>
          <span className="flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5" /> Desktop</span>
        </div>
        {/* Hotspot buttons are positioned by percentage over the flattened
            composite image below, so they scale with it at any width instead
            of needing per-breakpoint pixel offsets. Desktop's hotspot sits
            underneath (lower in DOM order = lower stacking by default) the
            phone/tablet ones so a tap in the corners where they visually
            overlap the desktop screen still hits whichever device is drawn
            on top in the image. */}
        <div className="relative w-full max-w-4xl mx-auto">
          <img
            src="/img/marketing/device-compatibility.webp"
            alt="QuoteHaul running on a phone, tablet, and desktop browser at once, all showing the same live Dashboard"
            className="w-full h-auto"
            loading="lazy"
          />
          <button
            type="button"
            onClick={() => setExpandedDeviceIdx(0)}
            aria-label="View desktop screenshot full size"
            className="absolute left-[12%] right-[24%] top-[1.5%] bottom-[3%] rounded-lg hover:bg-white/5 hover:ring-2 hover:ring-blue-400/50 transition cursor-zoom-in"
          />
          <button
            type="button"
            onClick={() => setExpandedDeviceIdx(1)}
            aria-label="View tablet screenshot full size"
            className="absolute left-[50.5%] right-0 top-[29%] bottom-0 rounded-lg hover:bg-white/5 hover:ring-2 hover:ring-blue-400/50 transition cursor-zoom-in"
          />
          <button
            type="button"
            onClick={() => setExpandedDeviceIdx(2)}
            aria-label="View phone screenshot full size"
            className="absolute left-0 right-[82.5%] top-[26.5%] bottom-0 rounded-lg hover:bg-white/5 hover:ring-2 hover:ring-blue-400/50 transition cursor-zoom-in"
          />
        </div>
      </section>

      {/* ── FEATURE SPOTLIGHT ───────────────────────────────────────────── */}
      {/* Merges what used to be two separate, largely redundant sections —
          see FEATURE_SPOTLIGHTS' own comment above. Alternates image side
          per row (even index = image right) for visual rhythm down the
          page rather than a static repeating layout. scroll-mt-20 offsets
          the anchor jump from the header nav's "Features" link so the
          sticky header doesn't cover the heading. */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 scroll-mt-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Everything a quote needs to become a shipment
        </h2>
        <p className="text-sm text-slate-400 text-center max-w-xl mx-auto mb-16">
          3D cargo visualization, freight analytics across your whole team, and paperwork that writes itself — the tools that take your operation to the next level.
        </p>
        <div className="space-y-16 sm:space-y-20">
          {FEATURE_SPOTLIGHTS.map((f, idx) => {
            const Icon = f.icon;
            const shots = f.shots.map(i => PRODUCT_TOUR_SHOTS[i]);
            const imageRight = idx % 2 === 0;
            return (
              <div key={f.title} className={`flex flex-col ${imageRight ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-8 md:gap-14`}>
                <div className="w-full md:w-1/2 min-w-0">
                  {shots.length === 1 ? (
                    <button
                      type="button"
                      onClick={() => setExpandedShotIdx(f.shots[0])}
                      className="block w-full cursor-zoom-in group"
                    >
                      <img
                        src={`/img/marketing/${shots[0].file}`}
                        alt={shots[0].alt}
                        loading={idx === 0 ? 'eager' : 'lazy'}
                        className="w-full h-auto transition-transform duration-300 group-hover:-translate-y-1"
                      />
                    </button>
                  ) : (
                    <div className={`grid gap-3 ${shots.length === 3 ? 'grid-cols-2' : 'grid-cols-2'}`}>
                      {shots.map((shot, sIdx) => (
                        <button
                          type="button"
                          key={shot.file}
                          onClick={() => setExpandedShotIdx(f.shots[sIdx])}
                          className={`cursor-zoom-in group ${shots.length === 3 && sIdx === 0 ? 'col-span-2' : ''}`}
                        >
                          <img
                            src={`/img/marketing/${shot.file}`}
                            alt={shot.alt}
                            loading="lazy"
                            className="w-full h-auto transition-transform duration-300 group-hover:-translate-y-1"
                          />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="w-full md:w-1/2 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-3 flex items-center gap-2 flex-wrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {f.title}
                    {f.badge && (
                      <span className="px-1.5 py-0.5 rounded-full bg-blue-600/20 border border-blue-500/40 text-blue-400 text-[9px] font-bold uppercase tracking-wider">
                        {f.badge}
                      </span>
                    )}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-4xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 scroll-mt-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Simple, per-organization pricing
        </h2>
        <p className="text-sm text-slate-400 text-center mb-8">One plan. Every feature. Add seats as your team grows.</p>

        <div className="flex justify-center mb-8">
          <div className="bg-slate-900 p-1 rounded-xl border border-slate-800 flex items-center gap-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition ${
                billingCycle === 'monthly' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 ${
                billingCycle === 'annual' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-white'
              }`}
            >
              <span>Annual</span>
              <span className="bg-emerald-500 text-black px-1.5 py-0.5 rounded text-[10px] font-bold uppercase">{ANNUAL_SAVINGS_LABEL}</span>
            </button>
          </div>
        </div>

        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border border-blue-500/30 rounded-2xl p-8 text-center shadow-2xl">
          <div className="text-5xl font-extrabold text-white mb-1">
            {formatUsd(basePrice)}<span className="text-lg font-semibold text-slate-400">/mo</span>
          </div>
          <p className="text-xs text-slate-500 mb-6">
            First seat included · additional seats {formatUsd(seatPrice)}/mo each
          </p>

          {/* Seat count chosen here is passed straight into TrialFlow as a
              prop, so it's already pre-filled at checkout — a visitor who
              knows they need e.g. 3 seats doesn't have to re-enter that a
              second time after creating their account. */}
          <div className="flex items-center justify-between gap-3 mb-4 bg-slate-800/60 p-3 rounded-xl border border-slate-700/60 max-w-sm mx-auto">
            <div className="text-left min-w-0">
              <div className="text-xs font-bold text-slate-200">Additional Team Seats</div>
              <div className="text-[11px] text-slate-500">Add more now, or later from Settings</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setExtraSeats(s => Math.max(0, s - 1))}
                className="w-7 h-7 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-6 text-center font-bold text-white text-sm">{extraSeats}</span>
              <button
                type="button"
                onClick={() => setExtraSeats(s => Math.min(50, s + 1))}
                className="w-7 h-7 flex items-center justify-center bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {extraSeats > 0 && (
            <div className="flex items-center justify-between gap-3 mb-6 px-1 max-w-sm mx-auto">
              <span className="text-xs font-bold text-slate-300 uppercase tracking-wide">Total after trial</span>
              <span className="text-lg font-extrabold text-white">{formatUsd(totalPrice)}/mo</span>
            </div>
          )}

          <ul className="text-left max-w-sm mx-auto space-y-2.5 mb-8">
            {[
              'Unlimited Cloud Sync & Quote Versioning',
              'Customer Directory & Vendor/Customer Rate Cards',
              'Default Rate Sheets & Freight Margin Analytics',
              'BOL & Invoice Generation',
              'Team Seats & Admin Controls',
            ].map((item, idx) => (
              <li key={idx} className="flex items-center gap-2.5 text-sm text-slate-200">
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => handleStartTrialClick('pricing')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-500/25 transition"
          >
            <span>Start Your {TRIAL_DAYS}-Day Free Trial</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ── DEMO MODE VS PRO COMPARISON ────────────────────────────────────
          Placed right after Pricing (not a separate page) — this is exactly
          what someone deciding whether to sign up wants to see next to the
          price itself. "Demo" here means the no-signup local demo mode, not
          a second paid tier — this app only has one paid plan (see above). */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          What You Get With Pro
        </h2>
        <p className="text-sm text-slate-400 text-center mb-8 max-w-lg mx-auto">
          Try the Freight Builder free with no signup, including the live 3D cargo visualization with AR on supported devices — and
          browse a sample-filled Organizations Directory, Rate Cards, and My Requests too. Pro unlocks a full team workspace:
          saving your own real data, BOL/invoice/label generation, document attachments, org-wide reporting, and
          role-based team management.
        </p>

        {/* table-fixed + percentage column widths (not the old unconditional
            min-w-[480px]) so the table always fits the actual viewport width
            on phones — the previous version forced a 480px-wide table
            regardless of screen size, cutting the "QuoteHaul Pro" column off
            the edge on anything narrower (e.g. iPhone Pro Max portrait,
            ~430px) with no visible affordance that it was scrollable.
            min-w-[480px] still applies from sm: up, preserving the original
            comfortable desktop sizing. */}
        <div className="overflow-x-auto">
          <table className="w-full sm:min-w-[480px] border-collapse text-sm table-fixed">
            <thead>
              <tr className="text-slate-500 uppercase text-[10px] tracking-wide">
                <th className="text-left font-bold pb-3 pr-2 sm:pr-4 w-[48%]">Feature</th>
                <th className="font-bold pb-3 px-1 sm:px-4 text-center w-[26%]">Demo</th>
                <th className="font-bold pb-3 pl-1 sm:pl-4 pr-1 sm:pr-6 text-center w-[26%]">
                  <span className="text-blue-400">QuoteHaul Pro</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, idx) => (
                <tr key={idx} className="border-t border-slate-800">
                  <td className="py-3 pr-2 sm:pr-4 text-slate-200 text-xs sm:text-sm">{row.label}</td>
                  <td className="py-3 px-1 sm:px-4 text-center">
                    {row.demo === true ? (
                      <Check className="w-4 h-4 text-emerald-400 mx-auto" />
                    ) : row.demo === false ? (
                      <X className="w-4 h-4 text-slate-600 mx-auto" />
                    ) : (
                      <span className="text-[10px] sm:text-[11px] text-slate-500">{row.demo}</span>
                    )}
                  </td>
                  <td className="py-3 pl-1 sm:pl-4 pr-1 sm:pr-6 text-center bg-blue-500/5">
                    {row.pro === true ? (
                      <Check className="w-4 h-4 text-emerald-400 mx-auto" />
                    ) : row.pro === false ? (
                      <X className="w-4 h-4 text-slate-600 mx-auto" />
                    ) : (
                      <span className="text-[10px] sm:text-[11px] text-slate-300">{row.pro}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="text-center mt-8">
          <button
            type="button"
            onClick={() => handleStartTrialClick('comparison_table')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold text-sm rounded-xl shadow-lg shadow-blue-500/25 transition"
          >
            <span>Start Your {TRIAL_DAYS}-Day Free Trial</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 py-14 border-t border-slate-800/80 scroll-mt-20">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center mb-8" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {FAQS.map((f, idx) => (
            <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
              <div className="text-sm font-bold text-white mb-1">{f.q}</div>
              <div className="text-sm text-slate-400">{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-slate-800/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <span>© {new Date().getFullYear()}{' '}
            <a href="https://xayvr.com" target="_blank" rel="noopener noreferrer" className="hover:text-slate-300 hover:underline transition">XAYVR Company, LLC</a>
            . All rights reserved.
          </span>
          <div className="flex items-center gap-4">
            <button onClick={() => setLegalDoc('terms')} className="hover:text-slate-300 hover:underline transition">Terms</button>
            <button onClick={() => setLegalDoc('privacy')} className="hover:text-slate-300 hover:underline transition">Privacy</button>
            <a href="/ops/" onClick={trackDemoStart} className="hover:text-slate-300 hover:underline transition">Try the Demo</a>
            {SOCIAL_LINKS.length > 0 && (
              <div className="flex items-center gap-3 pl-3 border-l border-slate-800">
                {SOCIAL_LINKS.map(({ href, label, Icon }) => (
                  <a key={href} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} title={label} className="text-slate-500 hover:text-slate-300 transition">
                    <Icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </footer>

      <LegalDocModal isOpen={!!legalDoc} onClose={() => setLegalDoc(null)} doc={legalDoc} />

      {expandedShotIdx !== null && (
        <ImageLightbox
          src={`/img/marketing/${PRODUCT_TOUR_SHOTS[expandedShotIdx].file}`}
          alt={PRODUCT_TOUR_SHOTS[expandedShotIdx].alt}
          title={PRODUCT_TOUR_SHOTS[expandedShotIdx].title}
          desc={PRODUCT_TOUR_SHOTS[expandedShotIdx].desc}
          onClose={() => setExpandedShotIdx(null)}
          onPrev={() => setExpandedShotIdx(i => (i - 1 + PRODUCT_TOUR_SHOTS.length) % PRODUCT_TOUR_SHOTS.length)}
          onNext={() => setExpandedShotIdx(i => (i + 1) % PRODUCT_TOUR_SHOTS.length)}
        />
      )}

      {expandedDeviceIdx !== null && (
        <ImageLightbox
          src={`/img/marketing/${DEVICE_SHOTS[expandedDeviceIdx].file}`}
          alt={DEVICE_SHOTS[expandedDeviceIdx].alt}
          title={DEVICE_SHOTS[expandedDeviceIdx].title}
          desc={DEVICE_SHOTS[expandedDeviceIdx].desc}
          onClose={() => setExpandedDeviceIdx(null)}
          onPrev={() => setExpandedDeviceIdx(i => (i - 1 + DEVICE_SHOTS.length) % DEVICE_SHOTS.length)}
          onNext={() => setExpandedDeviceIdx(i => (i + 1) % DEVICE_SHOTS.length)}
        />
      )}

      {trialFlowOpen && (
        <Suspense fallback={
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-md">
            <div className="w-8 h-8 border-2 border-slate-600 border-t-blue-500 rounded-full animate-spin" />
          </div>
        }>
          <TrialFlow
            initialBillingCycle={billingCycle}
            initialExtraSeats={extraSeats}
            onClose={() => setTrialFlowOpen(false)}
          />
        </Suspense>
      )}

      {/* Floating back-to-top — same blue as the header/hero CTA buttons
          (bg-blue-600), simple circle + chevron rather than matching their
          gradient, so it reads as a utility control, not another CTA
          competing for attention. z-20 matches the sticky header's own
          stacking context, high enough to sit above section content while
          scrolling. */}
      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
          title="Back to top"
          className="fixed bottom-6 right-6 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/30 transition"
        >
          <ChevronUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
